
import NumberplateForm from "@/components/utility/NumberplateForm.vue";
import { VehicleStateEnum } from "@/lib/enum/vehicleState.enum";
import { requiredRule } from "@/lib/rules/requiredRule";
import { vinOptionalRuleLength } from "@/lib/rules/vinRule";
import { calculateVinChecksum } from "@/lib/utility/calculateVinChecksum";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { handleError } from "@/lib/utility/handleError";
import { MrfiktivCreateVehicleDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import { IVehicle } from "@/models/vehicle.entity";

import { ConfigModule } from "@/store/modules/config";
import { PartnerModule } from "@/store/modules/partner";
import { VehicleModule } from "@/store/modules/vehicle.store";
import { mixins } from "vue-class-component";
import { Component, Prop, Vue } from "vue-property-decorator";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import Debug from "../utility/Debug.vue";
import RefsVehicle from "../utility/RefsVehicle.vue";
import { FleetModule } from "@/store/modules/fleet.store";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { FleetSearchModule } from "@/store/modules/search.store";
import { debounce } from "debounce";
import { UserModule } from "@/store/modules/me-user.store";

enum STEPS {
  ADD = "ADD",
  SUCCESS = "SUCCESS"
}

@Component({
  components: {
    ConfirmActionDialog,
    RefsVehicle,
    NumberplateForm,
    Debug
  }
})
export default class CreateVehicleDialog extends mixins(Vue) {
  ActionEnum = ActionEnum;
  ResourceEnum = ResourceEnum;

  VEHICLE_CREATED = "vehicleCreated";

  @Prop({ default: false })
  isDialogActive!: boolean;

  @Prop()
  partnerId!: string;

  @Prop()
  groupId?: string;

  @Prop({ default: false })
  isAutoClose?: boolean;

  readonly STEPS = STEPS;

  currentStep = STEPS.ADD;

  loading = false;

  isFormValid = false;

  createVehicleDto: MrfiktivCreateVehicleDtoGen = {
    displayName: "",
    numberplate: "",
    identificationnumber: ""
  };

  uId = 0;

  created: IVehicle[] = [];

  // Fleet search properties
  fleetSearchPattern = "";
  isSearchingFleets = false;
  debounceTimeout = 300;

  get filteredFleets() {
    return FleetSearchModule.filtered || [];
  }

  // Fleet search methods
  debounceFleetSearch = debounce((val: string) => {
    this.fleetSearchPattern = val;

    // Only perform backend search if we have at least 2 characters
    if (val && val.length >= 2) {
      this.searchFleets();
    }
  }, this.debounceTimeout);

  async searchFleets() {
    this.isSearchingFleets = true;

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.FLEET, this.partnerId)) {
        FleetSearchModule.setSearch(this.fleetSearchPattern);
        await FleetSearchModule.fetchPage({ partnerId: this.partnerId });
      }
    } catch (error) {
      this.$toast?.error &&
        this.$toast.error(this.$t("components.partner.CreateVehicleDialog.errorLoadingFleets").toString());
      this.$log.error("Error searching fleets:", error);
    } finally {
      this.isSearchingFleets = false;
    }
  }

  get isTrain() {
    return PartnerModule.isTrain;
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get vinRuleLength() {
    return [vinOptionalRuleLength()];
  }

  get debug() {
    return ConfigModule.debug;
  }

  get countryCode() {
    return PartnerModule?.partner?.countryCode ?? "DE";
  }

  get dialogModel() {
    return this.isDialogActive;
  }

  set dialogModel(v: boolean) {
    if (!v) {
      this.$emit("close");
    }
    this.$emit("update:isDialogActive", v);
  }

  get getChecksumText() {
    if (!this.createVehicleDto.identificationnumber || this.createVehicleDto.identificationnumber.length < 17) {
      return "";
    }

    return `${this.$t("common.nouns.checksum")}: ${calculateVinChecksum(
      this.createVehicleDto.identificationnumber || ""
    )}`;
  }

  get fleets() {
    return FleetModule.entities;
  }

  async rightClick() {
    switch (this.currentStep) {
      case STEPS.ADD: {
        if (!this.loading) {
          await this.add();
        }
        break;
      }

      case STEPS.SUCCESS: {
        this.reset();
        break;
      }
    }
  }

  async add() {
    try {
      this.loading = true;

      if (!this.createVehicleDto.identificationnumber && !this.createVehicleDto.numberplate) {
        this.createVehicleDto.state = VehicleStateEnum.UPCOMING;
      }

      this.createVehicleDto.groupId = this.groupId;

      const created = await VehicleModule.create({ partnerId: this.partnerId, data: this.createVehicleDto });

      if (this.isAutoClose) {
        this.close();

        this.$emit(this.VEHICLE_CREATED, created);

        return;
      }

      this.created.push(created);
      this.currentStep = STEPS.SUCCESS;
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  reset() {
    this.isFormValid = false;
    this.createVehicleDto = {
      displayName: "",
      numberplate: "",
      identificationnumber: ""
    };
    this.currentStep = STEPS.ADD;
    this.uId = Math.random();
  }

  close() {
    this.dialogModel = false;
    this.created.splice(0);
    this.reset();
  }

  async goToVehicleDetail(vehicle: IVehicle) {
    await new GoToHelper(this.$router).goToVehicleDetail(vehicle.id, vehicle.partnerId, undefined, true);
  }

  async mounted() {
    if (!UserModule.abilities.can(ActionEnum.READ, ResourceEnum.FLEET, this.partnerId)) {
      this.$log.info("No fleet permission");

      return;
    }

    if (!this.partnerId) {
      this.$log.error("No partner set");

      return;
    }

    try {
      await FleetSearchModule.fetchPage({ partnerId: this.partnerId });
    } catch (error) {
      this.$log.error("Error loading initial fleets:", error);
    }
  }
}
